import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  Card,
  CardContent,
  IconButton,
  Fab
} from '@mui/material';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Kitchen as KitchenIcon,
  AutoFixHigh as RecipeIcon,
  Timer as TimerIcon,
  People as ServingsIcon,
  BookmarkAdd as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { supabase } from '../utils/supabase';

const CUISINE_TYPES = [
  'Any',
  'Italian',
  'Mexican',
  'Asian',
  'Indian',
  'Mediterranean',
  'French',
  'American',
  'Thai',
  'Japanese',
  'Chinese',
  'Korean',
  'Vietnamese',
  'Middle Eastern',
  'Greek',
  'Spanish'
];

const DIETARY_RESTRICTIONS = [
  'None',
  'Vegetarian',
  'Vegan',
  'Gluten-Free',
  'Dairy-Free',
  'Nut-Free',
  'Low-Carb',
  'Keto',
  'Paleo',
  'Halal',
  'Kosher'
];

const MEAL_TYPES = [
  'Any',
  'Breakfast',
  'Lunch',
  'Dinner',
  'Snack',
  'Dessert',
  'Appetizer',
  'Side Dish'
];

export default function GenerateRecipePage({ session }) {
  const navigate = useNavigate();
  
  // State for ingredients
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [customIngredient, setCustomIngredient] = useState('');
  
  // State for recipe preferences
  const [cuisineType, setCuisineType] = useState('Any');
  const [dietaryRestriction, setDietaryRestriction] = useState('None');
  const [mealType, setMealType] = useState('Any');
  const [servings, setServings] = useState(4);
  const [cookingTime, setCookingTime] = useState('');
  const [customPreferences, setCustomPreferences] = useState('');
  
  // State for recipe generation
  const [loading, setLoading] = useState(false);
  const [generatedRecipes, setGeneratedRecipes] = useState([]);
  const [error, setError] = useState(null);
  
  // State for pantry ingredients loading
  const [pantryLoading, setPantryLoading] = useState(true);

  // Load pantry ingredients on component mount
  useEffect(() => {
    loadPantryIngredients();
  }, []);

  // Function to parse text-based recipe into structured format
  const parseRecipeText = (recipeText) => {
    const lines = recipeText.split('\n').filter(line => line.trim());
    const result = {
      name: '',
      description: '',
      ingredients: [],
      instructions: [],
      tips: [],
      macronutrients: {
        calories: '',
        protein: '',
        carbohydrates: '',
        fat: '',
        fiber: ''
      },
      prep_time: '',
      cook_time: '',
      servings: ''
    };

    let currentSection = 'description';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines
      if (!line) continue;

      // Extract recipe name (usually the first bold text or title)
      if (line.startsWith('**') && line.endsWith('**') && !result.name) {
        result.name = line.replace(/\*\*/g, '').trim();
        continue;
      }

      // More flexible section detection with multiple patterns
      const lowerLine = line.toLowerCase();
      
      // Ingredients section detection
      if (lowerLine.includes('ingredients') && (lowerLine.includes(':') || lowerLine.startsWith('**'))) {
        currentSection = 'ingredients';
        continue;
      }
      
      // Instructions section detection - look for multiple patterns
      if ((lowerLine.includes('instructions') || lowerLine.includes('directions') || lowerLine.includes('method') || lowerLine.includes('steps')) 
          && (lowerLine.includes(':') || lowerLine.startsWith('**'))) {
        currentSection = 'instructions';
        continue;
      }
      
      // Tips/Notes section detection
      if ((lowerLine.includes('tips') || lowerLine.includes('notes') || lowerLine.includes('variations') || lowerLine.includes('chef')) 
          && (lowerLine.includes(':') || lowerLine.startsWith('**'))) {
        currentSection = 'tips';
        continue;
      }
      
      // Macronutrients section detection
      if ((lowerLine.includes('macronutrients') || lowerLine.includes('nutrition') || lowerLine.includes('nutritional')) 
          && (lowerLine.includes(':') || lowerLine.startsWith('**'))) {
        currentSection = 'macronutrients';
        continue;
      }

      // Smart section switching based on content patterns
      // If we're in description but see numbered list or bullet points, it might be ingredients/instructions
      if (currentSection === 'description' && result.name && (line.match(/^\d+\./) || line.startsWith('*') || line.startsWith('-'))) {
        // If we have ingredients already, this is probably instructions
        if (result.ingredients.length > 0) {
          currentSection = 'instructions';
        } else {
          // First list we encounter is probably ingredients
          currentSection = 'ingredients';
        }
      }

      // Parse content based on current section
      if (currentSection === 'description' && !result.name) {
        // If no name found yet, first line might be the name
        if (i === 0) {
          result.name = line.replace(/\*\*/g, '').trim();
        } else {
          result.description += (result.description ? ' ' : '') + line.replace(/\*\*/g, '').trim();
        }
      } else if (currentSection === 'description' && result.name) {
        // Only add to description if it doesn't look like ingredients/instructions
        if (!line.match(/^\d+\./) && !line.startsWith('*') && !line.startsWith('-')) {
          result.description += (result.description ? ' ' : '') + line.replace(/\*\*/g, '').trim();
        }
      } else if (currentSection === 'ingredients') {
        // More flexible ingredient detection
        if (line.startsWith('*') || line.startsWith('-') || line.match(/^\d+\./) || 
            line.match(/^\d+\s+(cup|tbsp|tsp|oz|lb|g|kg|ml|l)/i) ||
            (line.length > 5 && !line.includes(':') && !lowerLine.includes('instructions'))) {
          const cleanLine = line.replace(/^[*\-\d.]\s*/, '').trim();
          if (cleanLine && !lowerLine.includes('instructions') && !lowerLine.includes('directions')) {
            result.ingredients.push(cleanLine);
          }
        }
      } else if (currentSection === 'instructions') {
        // More flexible instruction detection
        if (line.match(/^\d+\./) || line.startsWith('*') || line.startsWith('-') || 
            (line.length > 15 && !line.includes(':') && !lowerLine.includes('ingredients'))) {
          const cleanLine = line.replace(/^\d+\.\s*/, '').replace(/^[*\-]\s*/, '').replace(/\*\*/g, '').trim();
          if (cleanLine && !lowerLine.includes('ingredients')) {
            result.instructions.push(cleanLine);
          }
        }
      } else if (currentSection === 'tips') {
        if (line.startsWith('*') || line.startsWith('-') || line.length > 20) {
          result.tips.push(line.replace(/^[*\-]\s*/, '').replace(/\*\*/g, '').trim());
        }
      } else if (currentSection === 'macronutrients') {
        // Parse macronutrient information
        if (line.startsWith('*') || line.startsWith('-') || line.includes(':')) {
          const cleanLine = line.replace(/^[*\-]\s*/, '').replace(/\*\*/g, '').trim();
          const lowerCleanLine = cleanLine.toLowerCase();
          
          if (lowerCleanLine.includes('calories') || lowerCleanLine.includes('kcal')) {
            const match = cleanLine.match(/(\d+(?:\.\d+)?)/);
            if (match) result.macronutrients.calories = match[1];
          } else if (lowerCleanLine.includes('protein')) {
            const match = cleanLine.match(/(\d+(?:\.\d+)?)/);
            if (match) result.macronutrients.protein = match[1];
          } else if (lowerCleanLine.includes('carbohydrate') || lowerCleanLine.includes('carbs')) {
            const match = cleanLine.match(/(\d+(?:\.\d+)?)/);
            if (match) result.macronutrients.carbohydrates = match[1];
          } else if (lowerCleanLine.includes('fat')) {
            const match = cleanLine.match(/(\d+(?:\.\d+)?)/);
            if (match) result.macronutrients.fat = match[1];
          } else if (lowerCleanLine.includes('fiber') || lowerCleanLine.includes('fibre')) {
            const match = cleanLine.match(/(\d+(?:\.\d+)?)/);
            if (match) result.macronutrients.fiber = match[1];
          }
        }
      }
    }

    // Fallback for name if not found
    if (!result.name) {
      result.name = 'Generated Recipe';
    }

    // Post-processing: Clean up any misplaced items
    // Move obvious instructions from ingredients to instructions
    const cleanedIngredients = [];
    const additionalInstructions = [];
    
    result.ingredients.forEach(item => {
      const lowerItem = item.toLowerCase();
      // Check if this looks more like an instruction than an ingredient
      if (item.length > 80 || 
          lowerItem.includes('heat') || lowerItem.includes('cook') || lowerItem.includes('bake') ||
          lowerItem.includes('mix') || lowerItem.includes('stir') || lowerItem.includes('add') ||
          lowerItem.includes('place') || lowerItem.includes('pour') || lowerItem.includes('serve')) {
        additionalInstructions.push(item);
      } else {
        cleanedIngredients.push(item);
      }
    });
    
    result.ingredients = cleanedIngredients;
    
    // Move obvious ingredients from instructions to ingredients
    const cleanedInstructions = [];
    const additionalIngredients = [];
    
    result.instructions.forEach(item => {
      const lowerItem = item.toLowerCase();
      // Check if this looks more like an ingredient than an instruction
      if (item.length < 40 && 
          (item.match(/^\d+\s+(cup|tbsp|tsp|oz|lb|g|kg|ml|l)/i) ||
           (!lowerItem.includes('heat') && !lowerItem.includes('cook') && !lowerItem.includes('bake') &&
            !lowerItem.includes('mix') && !lowerItem.includes('stir') && !lowerItem.includes('place')))) {
        // Only move to ingredients if it contains typical ingredient indicators
        if (item.match(/\d+/) || lowerItem.includes('cup') || lowerItem.includes('tbsp') || 
            lowerItem.includes('tsp') || lowerItem.includes('oz') || lowerItem.includes('lb')) {
          additionalIngredients.push(item);
        } else {
          cleanedInstructions.push(item);
        }
      } else {
        cleanedInstructions.push(item);
      }
    });
    
    result.instructions = [...additionalInstructions, ...cleanedInstructions];
    result.ingredients = [...result.ingredients, ...additionalIngredients];

    // Extract timing information from description or instructions
    const timeRegex = /(\d+)\s*(minutes?|mins?|hours?|hrs?)/gi;
    const allText = result.description + ' ' + result.instructions.join(' ');
    const timeMatches = allText.match(timeRegex);
    if (timeMatches && timeMatches.length > 0) {
      result.cook_time = timeMatches[0];
    }

    return result;
  };

  const loadPantryIngredients = async () => {
    try {
      setPantryLoading(true);
      
      // Use direct REST API call to avoid session hanging issues
      const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/ingredients?user_id=eq.${session?.user?.id}&order=name.asc`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setAvailableIngredients(data || []);
    } catch (err) {
      console.error('Error loading pantry ingredients:', err);
      setError('Failed to load pantry ingredients');
    } finally {
      setPantryLoading(false);
    }
  };

  const addCustomIngredient = () => {
    const ingredient = customIngredient.trim();
    if (ingredient && !selectedIngredients.includes(ingredient)) {
      setSelectedIngredients([...selectedIngredients, ingredient]);
      setCustomIngredient('');
    }
  };

  const addPantryIngredient = (ingredient) => {
    if (!selectedIngredients.includes(ingredient.name)) {
      setSelectedIngredients([...selectedIngredients, ingredient.name]);
    }
  };

  const removeIngredient = (ingredientToRemove) => {
    setSelectedIngredients(selectedIngredients.filter(ing => ing !== ingredientToRemove));
  };

  const generateRecipe = async () => {
    if (selectedIngredients.length === 0) {
      setError('Please select at least one ingredient');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call our Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('generate-recipe', {
        body: {
          ingredients: selectedIngredients,
          preferences: {
            cuisine: cuisineType !== 'Any' ? cuisineType : undefined,
            dietary: dietaryRestriction !== 'None' ? dietaryRestriction : undefined,
            mealType: mealType !== 'Any' ? mealType : undefined,
            servings: servings,
            cookingTime: cookingTime || undefined,
            customPreferences: customPreferences.trim() || undefined
          }
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to generate recipe');
      }

      if (data && data.recipe) {
        const parsedRecipe = parseRecipeText(data.recipe);
        
        // Add unique ID and timestamp to the recipe
        const recipeWithId = {
          ...parsedRecipe,
          id: Date.now() + Math.random(), // Simple unique ID
          generatedAt: new Date().toLocaleString(),
          ingredients_used: selectedIngredients
        };
        
        // Add new recipe to the beginning of the array
        setGeneratedRecipes(prev => [recipeWithId, ...prev]);
      } else {
        throw new Error('No recipe data received');
      }
    } catch (err) {
      console.error('Recipe generation error:', err);
      setError(err.message || 'Failed to generate recipe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Function to replace a specific recipe
  const regenerateSpecificRecipe = async (recipeId) => {
    if (selectedIngredients.length === 0) {
      setError('Please select at least one ingredient');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call our Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('generate-recipe', {
        body: {
          ingredients: selectedIngredients,
          preferences: {
            cuisine: cuisineType !== 'Any' ? cuisineType : undefined,
            dietary: dietaryRestriction !== 'None' ? dietaryRestriction : undefined,
            mealType: mealType !== 'Any' ? mealType : undefined,
            servings: servings,
            cookingTime: cookingTime || undefined,
            customPreferences: customPreferences.trim() || undefined
          }
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to generate recipe');
      }

      if (data && data.recipe) {
        const parsedRecipe = parseRecipeText(data.recipe);
        
        // Create new recipe with same ID but updated content
        const updatedRecipe = {
          ...parsedRecipe,
          id: recipeId, // Keep the same ID
          generatedAt: new Date().toLocaleString(),
          ingredients_used: selectedIngredients
        };
        
        // Replace the specific recipe in the array
        setGeneratedRecipes(prev => 
          prev.map(recipe => recipe.id === recipeId ? updatedRecipe : recipe)
        );
      } else {
        throw new Error('No recipe data received');
      }
    } catch (err) {
      console.error('Recipe generation error:', err);
      setError(err.message || 'Failed to generate recipe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Function to save recipe to cookbook
  const saveRecipeToCookbook = async (recipe) => {
    try {
      // Prepare macronutrients as text for storage
      const macronutrientsText = recipe.macronutrients ? [
        recipe.macronutrients.calories ? `Calories: ${recipe.macronutrients.calories} kcal` : '',
        recipe.macronutrients.protein ? `Protein: ${recipe.macronutrients.protein} g` : '',
        recipe.macronutrients.carbohydrates ? `Carbohydrates: ${recipe.macronutrients.carbohydrates} g` : '',
        recipe.macronutrients.fat ? `Fat: ${recipe.macronutrients.fat} g` : '',
        recipe.macronutrients.fiber ? `Fiber: ${recipe.macronutrients.fiber} g` : ''
      ].filter(Boolean).join('\n') : '';

      // Use direct REST API call to save recipe instead of missing Edge Function
      const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/recipes`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify({
          user_id: session.user.id,
          title: recipe.name,
          ingredients_text: Array.isArray(recipe.ingredients) 
            ? recipe.ingredients.join('\n') 
            : recipe.ingredients || '',
          instructions_text: Array.isArray(recipe.instructions) 
            ? recipe.instructions.join('\n')
            : recipe.instructions || '',
          notes: [
            recipe.tips ? (Array.isArray(recipe.tips) ? recipe.tips.join('\n') : recipe.tips) : '',
            macronutrientsText ? `\n\nNutrition (per serving):\n${macronutrientsText}` : ''
          ].filter(Boolean).join('')
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const savedRecipe = await response.json();
      
    } catch (err) {
      console.error('Error saving recipe:', err);
      setError('Failed to save recipe to cookbook');
    }
  };

  // Function to delete a specific recipe
  const deleteRecipe = (recipeId) => {
    setGeneratedRecipes(prev => prev.filter(recipe => recipe.id !== recipeId));
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      addCustomIngredient();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <KitchenIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          AI Recipe Generator
        </Typography>
      </Box>

      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
        Select ingredients from your pantry or add custom ones, set your preferences, and let AI create a delicious recipe for you!
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
        {/* Left Column - Ingredient Selection */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <AddIcon sx={{ mr: 1 }} />
            Select Ingredients
          </Typography>

          {/* Custom Ingredient Input */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Add custom ingredient"
              variant="outlined"
              value={customIngredient}
              onChange={(e) => setCustomIngredient(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                endAdornment: (
                  <Button 
                    onClick={addCustomIngredient}
                    disabled={!customIngredient.trim()}
                    sx={{ ml: 1 }}
                  >
                    Add
                  </Button>
                )
              }}
            />
          </Box>

          {/* Pantry Ingredients */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            From Your Pantry:
          </Typography>
          
          {pantryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : availableIngredients.length > 0 ? (
            <Box sx={{ mb: 3, maxHeight: 200, overflowY: 'auto' }}>
              {availableIngredients.map((ingredient) => (
                <Chip
                  key={ingredient.id}
                  label={ingredient.name}
                  onClick={() => addPantryIngredient(ingredient)}
                  sx={{ m: 0.5 }}
                  color={selectedIngredients.includes(ingredient.name) ? 'primary' : 'default'}
                  variant={selectedIngredients.includes(ingredient.name) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              No ingredients in your pantry. Add some ingredients manually above.
            </Typography>
          )}

          {/* Selected Ingredients */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Selected Ingredients ({selectedIngredients.length}):
          </Typography>
          
          {selectedIngredients.length > 0 ? (
            <Box sx={{ mb: 2 }}>
              {selectedIngredients.map((ingredient) => (
                <Chip
                  key={ingredient}
                  label={ingredient}
                  onDelete={() => removeIngredient(ingredient)}
                  deleteIcon={<DeleteIcon />}
                  sx={{ m: 0.5 }}
                  color="primary"
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No ingredients selected yet.
            </Typography>
          )}
        </Paper>

        {/* Right Column - Recipe Preferences */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <RecipeIcon sx={{ mr: 1 }} />
            Recipe Preferences
          </Typography>

          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Cuisine Type</InputLabel>
              <Select
                value={cuisineType}
                onChange={(e) => setCuisineType(e.target.value)}
                label="Cuisine Type"
              >
                {CUISINE_TYPES.map((cuisine) => (
                  <MenuItem key={cuisine} value={cuisine}>{cuisine}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Dietary Restrictions</InputLabel>
              <Select
                value={dietaryRestriction}
                onChange={(e) => setDietaryRestriction(e.target.value)}
                label="Dietary Restrictions"
              >
                {DIETARY_RESTRICTIONS.map((diet) => (
                  <MenuItem key={diet} value={diet}>{diet}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Meal Type</InputLabel>
              <Select
                value={mealType}
                onChange={(e) => setMealType(e.target.value)}
                label="Meal Type"
              >
                {MEAL_TYPES.map((meal) => (
                  <MenuItem key={meal} value={meal}>{meal}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Servings"
                type="number"
                value={servings}
                onChange={(e) => setServings(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1, max: 20 }}
                InputProps={{
                  startAdornment: <ServingsIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
              
              <TextField
                label="Max Cooking Time"
                placeholder="e.g., 30 minutes"
                value={cookingTime}
                onChange={(e) => setCookingTime(e.target.value)}
                InputProps={{
                  startAdornment: <TimerIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Box>

            <TextField
              label="Custom Preferences"
              placeholder="e.g., make it spicy, use fresh herbs, kid-friendly, low-sodium..."
              value={customPreferences}
              onChange={(e) => setCustomPreferences(e.target.value)}
              multiline
              rows={3}
              fullWidth
              helperText="Add any special preferences or requirements for your recipe"
            />
          </Box>

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={generateRecipe}
            disabled={loading || selectedIngredients.length === 0}
            sx={{ mt: 3, py: 1.5 }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Generating Recipe...
              </>
            ) : (
              'Generate Recipe'
            )}
          </Button>
        </Paper>
      </Box>

      {/* Generated Recipes Display */}
      {generatedRecipes.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
            <KitchenIcon sx={{ mr: 1, color: 'primary.main' }} />
            Generated Recipes ({generatedRecipes.length})
          </Typography>
          
          {generatedRecipes.map((recipe, recipeIndex) => (
            <Paper key={recipe.id} sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <KitchenIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                  {recipe.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => saveRecipeToCookbook(recipe)}
                    sx={{ color: 'white' }}
                  >
                    Save to Cookbook
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => regenerateSpecificRecipe(recipe.id)}
                    disabled={loading || selectedIngredients.length === 0}
                  >
                    {loading ? 'Generating...' : 'Generate Another'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => deleteRecipe(recipe.id)}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Generated: {recipe.generatedAt} • Ingredients: {recipe.ingredients_used.join(', ')}
              </Typography>

              {recipe.description && (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {recipe.description}
                </Typography>
              )}

              <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' } }}>
                {/* Recipe Info & Ingredients */}
                <Box>
                  {/* Recipe Info */}
                  {(recipe.prep_time || recipe.cook_time || recipe.servings) && (
                    <Card sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 1 }}>Recipe Info</Typography>
                        {recipe.prep_time && (
                          <Typography variant="body2">Prep Time: {recipe.prep_time}</Typography>
                        )}
                        {recipe.cook_time && (
                          <Typography variant="body2">Cook Time: {recipe.cook_time}</Typography>
                        )}
                        {recipe.servings && (
                          <Typography variant="body2">Servings: {recipe.servings}</Typography>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Macronutrients */}
                  {recipe.macronutrients && (recipe.macronutrients.calories || recipe.macronutrients.protein || recipe.macronutrients.carbohydrates || recipe.macronutrients.fat || recipe.macronutrients.fiber) && (
                    <Card sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 1 }}>Nutrition (per serving)</Typography>
                        {recipe.macronutrients.calories && (
                          <Typography variant="body2">Calories: {recipe.macronutrients.calories} kcal</Typography>
                        )}
                        {recipe.macronutrients.protein && (
                          <Typography variant="body2">Protein: {recipe.macronutrients.protein} g</Typography>
                        )}
                        {recipe.macronutrients.carbohydrates && (
                          <Typography variant="body2">Carbohydrates: {recipe.macronutrients.carbohydrates} g</Typography>
                        )}
                        {recipe.macronutrients.fat && (
                          <Typography variant="body2">Fat: {recipe.macronutrients.fat} g</Typography>
                        )}
                        {recipe.macronutrients.fiber && (
                          <Typography variant="body2">Fiber: {recipe.macronutrients.fiber} g</Typography>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Ingredients */}
                  {recipe.ingredients && recipe.ingredients.length > 0 && (
                    <Card>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>Ingredients</Typography>
                        {recipe.ingredients.map((ingredient, index) => (
                          <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                            • {ingredient}
                          </Typography>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </Box>

                {/* Instructions */}
                {recipe.instructions && recipe.instructions.length > 0 && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>Instructions</Typography>
                      {recipe.instructions.map((instruction, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="primary">
                            Step {index + 1}
                          </Typography>
                          <Typography variant="body2">
                            {instruction}
                          </Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </Box>

              {/* Tips */}
              {recipe.tips && recipe.tips.length > 0 && (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Tips</Typography>
                    {recipe.tips.map((tip, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                        • {tip}
                      </Typography>
                    ))}
                  </CardContent>
                </Card>
              )}
            </Paper>
          ))}
        </Box>
      )}

      {/* Floating Action Button for Quick Access */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => navigate('/pantry')}
        aria-label="Go to pantry"
      >
        <KitchenIcon />
      </Fab>
    </Container>
  );
}
